import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../api.js';
import { useAuth } from '../../auth.jsx';
import { useI18n } from '../../i18n/index.jsx';
import { Alert, Field, PasswordInput } from '../../components/ui.jsx';
import PhoneInput from '../../components/PhoneInput.jsx';
import PasswordStrength from '../../components/PasswordStrength.jsx';
import AuthLayout from '../../components/auth/AuthLayout.jsx';
import GoogleButton from '../../components/auth/GoogleButton.jsx';
import CodeStep from '../../components/auth/CodeStep.jsx';
import { useCodeFlow } from '../../components/auth/useCodeFlow.js';
import { afterSignIn } from './postLogin.js';
import { formatDate } from '../../format.js';

// Sign-up in three steps on one page:
// 1. email + privacy consent → a 6-digit code is emailed (for an address
//    that already has an account, that email says so instead — the screen
//    is identical, so it never reveals who is a member);
// 2. the code;
// 3. password, name, company, phone → account created (verified), signed in.
// Google sign-up skips steps 1–2.
// /inscription?invite=CODE (invite link): same steps with an "free Seller
// access" banner; the code is checked again by the server when the account
// is created. A signed-in member gets an "Activate" button instead.
export default function Signup() {
  const { t, lang, errorText } = useI18n();
  const { signIn, user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next');
  const flow = useCodeFlow('signup');
  const inviteCode = params.get('invite') || '';
  const invite = useInvite(inviteCode);
  const inviteBody = invite?.valid ? { invite: inviteCode } : {};
  const [consent, setConsent] = useState(false);
  const [form, setForm] = useState({ password: '', name: '', companyName: '', phone: '' });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const finish = ({ token, user }) => {
    signIn(token, user);
    navigate(afterSignIn(user, next), { replace: true });
  };

  async function createAccount(e) {
    e.preventDefault();
    flow.setError('');
    if (form.password.length < 8) return flow.setError(t('errors.password_too_short'));
    if (form.phone.replace(/\D/g, '').length !== 10) return flow.setError(t('errors.invalid_phone'));
    flow.setBusy(true);
    try {
      finish(await api('/auth/signup/complete', { method: 'POST', body: { ...form, ...inviteBody, ticket: flow.ticket, lang } }));
    } catch (err) {
      flow.setError(errorText(err));
      if (err.code === 'ticket_invalid') flow.changeEmail();
      flow.setBusy(false);
    }
  }

  // After signing in, an existing member comes back here to activate it.
  const loginNext = inviteCode ? `/inscription?invite=${encodeURIComponent(inviteCode)}` : next;
  const below = <>{t('auth.haveAccount')} <Link to={`/login${loginNext ? `?next=${encodeURIComponent(loginNext)}` : ''}`}>{t('auth.signinTitle')}</Link></>;
  const banner = inviteCode ? <InviteBanner invite={invite} /> : null;

  if (inviteCode && user) {
    return (
      <AuthLayout titleKey="invite.activateTitle" title={t('invite.activateTitle')} lead={t('invite.activateLead', { name: user.name })}>
        <ActivateInvite code={inviteCode} invite={invite} />
      </AuthLayout>
    );
  }

  if (flow.step === 'code') {
    return (
      <AuthLayout titleKey="auth.codeTitle" title={t('auth.codeTitle')} lead={t('auth.codeLead')} below={below}>
        {banner}
        <CodeStep flow={flow} onResend={() => flow.send({ consent: true })} />
      </AuthLayout>
    );
  }

  if (flow.step === 'final') {
    return (
      <AuthLayout titleKey="auth.createTitle" title={t('auth.createTitle')} lead={t('auth.createLead', { email: flow.email.trim() })}>
        {banner}
        <form onSubmit={createAccount} className="form" noValidate>
          <Field label={t('auth.email')} htmlFor="sc-email">
            <input id="sc-email" type="email" value={flow.email.trim()} readOnly className="is-readonly" autoComplete="username" />
          </Field>
          <Field label={t('auth.choosePassword')} htmlFor="sc-password">
            <PasswordInput id="sc-password" value={form.password} onChange={set('password')} autoComplete="new-password" required minLength={8} autoFocus />
            <PasswordStrength password={form.password} />
          </Field>
          <div className="grid-form">
            <Field label={t('auth.name')} htmlFor="sc-name">
              <input id="sc-name" value={form.name} onChange={set('name')} autoComplete="name" required maxLength={120} />
            </Field>
            <Field label={t('auth.companyName')} htmlFor="sc-company">
              <input id="sc-company" value={form.companyName} onChange={set('companyName')} autoComplete="organization" required maxLength={160} />
            </Field>
          </div>
          <Field label={t('auth.phone')} htmlFor="sc-phone">
            <PhoneInput id="sc-phone" value={form.phone} onChange={(v) => setForm((f) => ({ ...f, phone: v }))} required />
          </Field>
          <Alert>{flow.error}</Alert>
          <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={flow.busy}>
            {flow.busy ? t('common.saving') : t('auth.createSubmit')}
          </button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout titleKey="auth.signupTitle" title={t('auth.signupTitle')} lead={t('auth.signupLead')} below={below}>
      {banner}
      <GoogleButton text="signup_with" divider="after" onSignedIn={finish} extra={inviteBody} />
      <form onSubmit={(e) => { e.preventDefault(); if (!consent) return flow.setError(t('errors.consent_required')); flow.send({ consent: true }); }} className="form" noValidate>
        <Field label={t('auth.email')} htmlFor="su-email">
          <input id="su-email" type="email" value={flow.email} onChange={(e) => flow.setEmail(e.target.value)} autoComplete="email" required />
        </Field>
        <ConsentCheckbox checked={consent} onChange={setConsent} />
        <Alert>{flow.error}</Alert>
        <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={flow.busy}>
          {flow.busy ? t('common.sending') : t('auth.continue')}
        </button>
        <p className="field-hint center">{t('auth.signupEmailHint')}</p>
      </form>
    </AuthLayout>
  );
}

// Invite link details from the server (never trusted for the grant itself).
function useInvite(code) {
  const [invite, setInvite] = useState(null);
  useEffect(() => {
    if (!code) { setInvite(null); return undefined; }
    let alive = true;
    fetch(`/api/invites/${encodeURIComponent(code)}`, { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : { valid: false, reason: r.status === 429 ? 'busy' : 'not_found' }))
      .then((d) => { if (alive) setInvite(d); })
      .catch(() => { if (alive) setInvite({ valid: false, reason: 'busy' }); });
    return () => { alive = false; };
  }, [code]);
  return invite;
}

function durationText(invite, t, lang) {
  if (invite.durationKind === 'months') return t('invite.forMonths', { n: invite.months });
  if (invite.durationKind === 'until') return t('invite.until', { date: formatDate(`${invite.until}T12:00:00`, lang) });
  return t('invite.permanent');
}

function InviteBanner({ invite }) {
  const { t, lang } = useI18n();
  if (!invite) return <div className="invite-banner is-loading" aria-busy="true">{t('common.loading')}</div>;
  if (!invite.valid) {
    return (
      <div className="invite-banner is-invalid" role="status">
        <strong>{t(`invite.problem.${invite.reason}`)}</strong>
        <span>{t('invite.stillSilver')}</span>
      </div>
    );
  }
  return (
    <div className="invite-banner" role="status">
      <span className="invite-banner-icon" aria-hidden="true">★</span>
      <span>
        <strong>{t('invite.bannerTitle')}</strong>
        <span>{t('invite.bannerText', { duration: durationText(invite, t, lang) })}{invite.personal ? ` ${t('invite.personal')}` : ''}</span>
      </span>
    </div>
  );
}

// Signed-in member opening an invite link: one button applies it.
function ActivateInvite({ code, invite }) {
  const { t, errorText } = useI18n();
  const { setUser, user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);
  async function activate() {
    setBusy(true); setError('');
    try {
      const r = await api(`/invites/${encodeURIComponent(code)}/activate`, { method: 'POST' });
      setUser(r.user);
      setDone(r.membership.complimentary);
    } catch (err) { setError(errorText(err)); } finally { setBusy(false); }
  }
  if (done) {
    return (
      <div className="form">
        <div className="invite-banner is-done" role="status"><span className="invite-banner-icon" aria-hidden="true">✓</span><span><strong>{t('invite.activated')}</strong><span>{t('invite.activatedText')}</span></span></div>
        <Link to="/account/plan" className="btn btn-primary btn-lg btn-block">{t('invite.goToAccount')}</Link>
      </div>
    );
  }
  return (
    <div className="form">
      <InviteBanner invite={invite} />
      {user.role === 'admin' && <p className="muted small">{t('invite.adminNote')}</p>}
      <Alert>{error}</Alert>
      {invite?.valid && user.role !== 'admin' && (
        <button type="button" className="btn btn-primary btn-lg btn-block" onClick={activate} disabled={busy}>{busy ? t('common.saving') : t('invite.activate')}</button>
      )}
      <Link to="/account" className="btn btn-link btn-block">{t('invite.notNow')}</Link>
    </div>
  );
}

export function ConsentCheckbox({ checked, onChange }) {
  const { t } = useI18n();
  return (
    <label className="checkbox">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>
        {t('auth.consentBefore')}
        <Link to="/privacy" target="_blank" rel="noopener">{t('auth.consentLink')}</Link>
        {t('auth.consentAfter')}
      </span>
    </label>
  );
}
